import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Text, TextVariant, Button } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { ethers } from 'ethers'
import { Contract } from '@ethersproject/contracts'
import { QRCodeSVG } from 'qrcode.react'
import CopyToClipboard from 'react-copy-to-clipboard'
import { ArrowUpRight, ArrowDownLeft, Check, ChevronDown, Copy, X } from 'react-feather'
import { useWeb3ProviderAndContract } from 'hooks/useWeb3ProviderAndContract'
import { useSwitchChainForContractTx } from 'hooks/useSwitchChainForContractTx'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { MASTER_DISTRIBUTOR_MIN_ABI } from 'utils/marketplace/abi'
import useToast from 'hooks/useToast'
import type { WalletPanelBalanceRow } from 'hooks/useWalletPanelOnchainBalances'
import { getErrorCodesDescription } from 'utils/getErrorCodesDescription'

const ERC20_TRANSFER_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
  'function balanceOf(address owner) view returns (uint256)'
]

/**
 * The master distributor decides how much of an MPRO transfer is burned, per
 * sender. Named here for the same reason the marketplace names it: the token
 * exposes no getter and a sender who is whitelisted burns nothing, so the
 * number has to be read for this wallet rather than assumed.
 */
const MASTER_DISTRIBUTOR = '0x568cd600a92b26f848804e4005822f7d28753632'

/**
 * Held back when taking Max of a native balance. Sending the whole of it
 * always fails — there is nothing left to pay the fee with — so Max on ETH
 * means "all of it that can actually be sent". Base fees are fractions of a
 * cent, so this is deliberately generous rather than precise: a leftover
 * fraction of a cent costs nothing, a failed transaction costs a fee.
 */
const NATIVE_GAS_RESERVE = ethers.utils.parseEther('0.00005')

const Actions = styled.div`
  display: flex;
  gap: 10px;
`

const ActionButton = styled.button`
  display: inline-flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.backgroundContrast};
  color: ${({ theme }) => theme.colors.white};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.purple};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const Sheet = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.backgroundContrast};
`

const SheetHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const CloseButton = styled.button`
  display: inline-flex;
  padding: 2px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.gray6};
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.colors.white};
  }
`

const Field = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-family: inherit;
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

/*
 * Hand-built rather than a native <select>. The browser renders a select's
 * option list with the operating system's own widget — a white panel in a
 * black app, immune to every style here. The only way for the list to look
 * like the product is for the list to be part of the product.
 */
const PickerWrap = styled.div`
  position: relative;
  width: 100%;
`

const PickerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-family: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray6};
  }
`

const PickerList = styled.div`
  position: absolute;
  z-index: 40;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  padding: 4px;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.backgroundContrast};
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.6);
`

const PickerOption = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 10px;
  border: none;
  border-radius: 7px;
  background: ${({ theme, $selected }) => ($selected ? `${theme.colors.purple}26` : 'transparent')};
  color: ${({ theme }) => theme.colors.white};
  font-family: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.colors.gray9};
  }
`

const OptionChain = styled.span`
  color: ${({ theme }) => theme.colors.gray6};
  font-size: 11px;
`

const AmountRow = styled.div`
  position: relative;
  width: 100%;
`

const MaxButton = styled.button`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.backgroundContrast};
  color: ${({ theme }) => theme.colors.purple};
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.purple};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const QrFrame = styled.div`
  display: flex;
  justify-content: center;
  padding: 14px;
  border-radius: 12px;
  background: #ffffff;
`

const AddressBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  word-break: break-all;
  cursor: pointer;
`

/**
 * The full address, never truncated. A confirmation that shows `0x25b9…5B03`
 * proves only that the first and last characters survived — the middle is
 * exactly where a swapped address hides, and this transfer cannot be undone.
 */
const FullAddress = styled.code`
  flex: 1;
  color: ${({ theme }) => theme.colors.white};
  font-size: 12px;
  line-height: 1.5;
  word-break: break-all;
`

const Line = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`

type Mode = 'none' | 'send' | 'receive'

interface WalletSendReceiveProps {
  account: string
  rows: WalletPanelBalanceRow[]
  walletChainId: number | undefined
  onSent: () => void
}

const WalletSendReceive: React.FC<WalletSendReceiveProps> = ({ account, rows, walletChainId, onSent }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { toastError, toastSuccess } = useToast()
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()
  const ensureChainForTx = useSwitchChainForContractTx()

  const [mode, setMode] = useState<Mode>('none')
  const [rowId, setRowId] = useState('mpro-base')
  const [to, setTo] = useState('')
  const [amount, setAmount] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [burnWei, setBurnWei] = useState<ethers.BigNumber | null>(null)

  const row = useMemo(() => rows.find((r) => r.id === rowId), [rows, rowId])

  /**
   * Sending is refused over a balance that could not be read. The panel already
   * distinguishes "zero" from "unknown"; this is what that distinction is for —
   * a form that offers a Max button over an unknown balance is inviting a
   * failed transaction at best.
   */
  const balanceUnknown = Boolean(row?.failed)

  const amountWei = useMemo(() => {
    try {
      const v = ethers.utils.parseEther((amount || '0').trim())
      return v.lte(0) ? null : v
    } catch {
      return null
    }
  }, [amount])

  const recipient = useMemo(() => {
    const raw = to.trim()
    if (!raw) return null
    try {
      // Rejects a bad checksum outright rather than silently normalising it,
      // because a mistyped character that happens to parse is the whole risk.
      return ethers.utils.getAddress(raw)
    } catch {
      return null
    }
  }, [to])

  const sendingToSelf = Boolean(recipient && recipient.toLowerCase() === account.toLowerCase())

  /** MPRO burns on transfer; ETH does not. Read per sender, never assumed. */
  useEffect(() => {
    let cancelled = false
    setBurnWei(null)
    if (!row || row.symbol !== 'MPRO' || !amountWei) return undefined
    ;(async () => {
      try {
        const rpc = getSimpleRpcProviderByChainId(row.chainId)
        const distributor = new Contract(
          MASTER_DISTRIBUTOR,
          MASTER_DISTRIBUTOR_MIN_ABI as unknown as ethers.ContractInterface,
          rpc
        )
        const burn = await distributor.getBurnAmount(account, amountWei)
        if (!cancelled) setBurnWei(burn)
      } catch {
        // Unreadable burn stays null and is reported as unknown, not as zero.
        if (!cancelled) setBurnWei(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [account, amountWei, row])

  const maxWei = useMemo(() => {
    if (!row?.amountWei) return null
    const full = ethers.BigNumber.from(row.amountWei)
    if (row.tokenAddress) return full.gt(0) ? full : null
    const spendable = full.sub(NATIVE_GAS_RESERVE)
    return spendable.gt(0) ? spendable : null
  }, [row])

  const applyMax = useCallback(() => {
    if (!maxWei) return
    setAmount(ethers.utils.formatEther(maxWei))
  }, [maxWei])

  const reset = useCallback(() => {
    setMode('none')
    setConfirming(false)
    setTo('')
    setAmount('')
    setBurnWei(null)
    setPickerOpen(false)
  }, [])

  const onSend = useCallback(async () => {
    if (!row || !recipient || !amountWei) return
    setBusy(true)
    try {
      const ok = await ensureChainForTx(row.chainId)
      if (!ok) {
        /*
         * Saying nothing here is the same defect as a wallet call that shows
         * nothing: the user presses Send and the button appears dead. And this
         * is not a rare path — WalletConnect cannot switch chains
         * programmatically at all ("does not support programmatic chain
         * switching", already recorded against /rewards from mobile), so for
         * those wallets the only way through is to switch by hand.
         */
        toastError(t('walletPanel.switchNetworkYourself', { chain: row.chainLabel }))
        return
      }
      /*
       * The account argument is what makes this the WALLET's signer. Without
       * it the helper returns a chain-selected read-only provider, which holds
       * nobody's keys — the recorded cause of an `unknown account` that read
       * like a flaky node and was us asking a stranger to sign.
       */
      const signer = getProviderOrSignerByChainId(row.chainId, account)

      let tx
      if (row.tokenAddress) {
        const token = new Contract(row.tokenAddress, ERC20_TRANSFER_ABI, signer)
        tx = await token.transfer(recipient, amountWei)
      } else {
        tx = await (signer as ethers.providers.JsonRpcSigner).sendTransaction({
          to: recipient,
          value: amountWei
        })
      }
      await tx.wait()
      toastSuccess(t('walletPanel.sendDone', { symbol: row.symbol }))
      reset()
      onSent()
    } catch (e) {
      const msg = (e as { message?: string })?.message ?? ''
      // Declining in the wallet is a decision, not a failure to report back.
      if (!/user rejected|user denied|ACTION_REJECTED/i.test(msg)) {
        toastError(t('walletPanel.sendFailed'), getErrorCodesDescription(t, e))
      }
    } finally {
      setBusy(false)
    }
  }, [
    account,
    row,
    recipient,
    amountWei,
    ensureChainForTx,
    getProviderOrSignerByChainId,
    toastSuccess,
    toastError,
    t,
    reset,
    onSent
  ])

  if (mode === 'none') {
    return (
      <Actions>
        <ActionButton type="button" onClick={() => setMode('send')}>
          <ArrowUpRight size={15} />
          {t('walletPanel.send')}
        </ActionButton>
        <ActionButton type="button" onClick={() => setMode('receive')}>
          <ArrowDownLeft size={15} />
          {t('walletPanel.receive')}
        </ActionButton>
      </Actions>
    )
  }

  if (mode === 'receive') {
    return (
      <Sheet>
        <SheetHead>
          <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('walletPanel.receive')}</Text>
          <CloseButton type="button" onClick={reset} aria-label={t('walletPanel.close')}>
            <X size={16} />
          </CloseButton>
        </SheetHead>
        <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
          {t('walletPanel.receiveHint')}
        </Text>
        <QrFrame>
          <QRCodeSVG value={account} size={148} level="M" />
        </QrFrame>
        <CopyToClipboard text={account} onCopy={() => setCopied(true)}>
          <AddressBox title={t('walletPanel.copyAddress')}>
            <FullAddress>{account}</FullAddress>
            {copied ? <Check size={15} color={colors.green} /> : <Copy size={15} color={colors.gray6} />}
          </AddressBox>
        </CopyToClipboard>
      </Sheet>
    )
  }

  return (
    <Sheet>
      <SheetHead>
        <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('walletPanel.send')}</Text>
        <CloseButton type="button" onClick={reset} aria-label={t('walletPanel.close')}>
          <X size={16} />
        </CloseButton>
      </SheetHead>

      {!confirming ? (
        <>
          <PickerWrap>
            <PickerButton
              type="button"
              onClick={() => setPickerOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={pickerOpen}
            >
              <span>
                {row?.symbol} <OptionChain>({row?.chainLabel})</OptionChain>
              </span>
              <span>
                {row?.amountDisplay} <ChevronDown size={14} />
              </span>
            </PickerButton>
            {pickerOpen && (
              <PickerList role="listbox">
                {rows.map((r) => (
                  <PickerOption
                    key={r.id}
                    type="button"
                    role="option"
                    aria-selected={r.id === rowId}
                    $selected={r.id === rowId}
                    onClick={() => {
                      setRowId(r.id)
                      setPickerOpen(false)
                    }}
                  >
                    <span>
                      {r.symbol} <OptionChain>({r.chainLabel})</OptionChain>
                    </span>
                    <span>{r.amountDisplay}</span>
                  </PickerOption>
                ))}
              </PickerList>
            )}
          </PickerWrap>

          <Field
            placeholder={t('walletPanel.recipientPlaceholder')}
            value={to}
            onChange={(e) => setTo(e.target.value)}
            spellCheck={false}
          />
          {to.trim() && !recipient && (
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.red}>
              {t('walletPanel.badAddress')}
            </Text>
          )}
          {sendingToSelf && (
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.yellow}>
              {t('walletPanel.sendingToSelf')}
            </Text>
          )}

          <AmountRow>
            <Field
              placeholder={t('walletPanel.amountPlaceholder', { symbol: row?.symbol ?? '' })}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              style={{ paddingRight: 64 }}
            />
            <MaxButton
              type="button"
              onClick={applyMax}
              disabled={!maxWei}
              title={row?.tokenAddress ? undefined : t('walletPanel.maxKeepsGas')}
            >
              {t('walletPanel.max')}
            </MaxButton>
          </AmountRow>
          {!row?.tokenAddress && maxWei && (
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {t('walletPanel.maxKeepsGas')}
            </Text>
          )}

          {balanceUnknown && (
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.red}>
              {t('walletPanel.cannotSendUnknownBalance')}
            </Text>
          )}

          {row && walletChainId !== undefined && walletChainId !== row.chainId && (
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {t('walletPanel.willSwitchNetwork', { chain: row.chainLabel })}
            </Text>
          )}

          <Button
            type="button"
            variant="primary"
            size="md"
            disabled={!recipient || !amountWei || balanceUnknown || busy}
            onClick={() => setConfirming(true)}
          >
            {t('walletPanel.review')}
          </Button>
        </>
      ) : (
        <>
          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
            {t('walletPanel.confirmIntro')}
          </Text>
          <AddressBox as="div" style={{ cursor: 'default' }}>
            <FullAddress>{recipient}</FullAddress>
          </AddressBox>
          <Line>
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {t('walletPanel.amountLabel')}
            </Text>
            <Text variant={TextVariant.BODY_SMALL_BOLD}>
              {amount} {row?.symbol}
            </Text>
          </Line>
          <Line>
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {t('walletPanel.networkLabel')}
            </Text>
            <Text variant={TextVariant.BODY_SMALL_BOLD}>{row?.chainLabel}</Text>
          </Line>
          {row?.symbol === 'MPRO' && (
            <Line>
              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                {t('walletPanel.theyReceive')}
              </Text>
              <Text variant={TextVariant.BODY_SMALL_BOLD} color={colors.cyan}>
                {burnWei && amountWei
                  ? `${ethers.utils.formatEther(amountWei.sub(burnWei))} MPRO`
                  : t('walletPanel.burnUnknown')}
              </Text>
            </Line>
          )}
          <Button type="button" variant="primary" size="md" disabled={busy} onClick={() => void onSend()}>
            {busy ? t('walletPanel.sending') : t('walletPanel.confirmSend')}
          </Button>
          <Button type="button" variant="secondary" size="md" disabled={busy} onClick={() => setConfirming(false)}>
            {t('walletPanel.back')}
          </Button>
        </>
      )}
    </Sheet>
  )
}

export default WalletSendReceive
